import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { Building } from '../../classes/building';
import { BillingBuilding, BillingService } from "../../shared/billing.service";
import { DataTable, IDataClassConfig } from 'app/classes/data-table/data-table';

@Component({
  selector: 'app-billing-buildings-list',
  templateUrl: './billing-buildings-list.component.html',
  styleUrls: ['./billing-buildings-list.component.css'],
  standalone: false
})
export class BillingBuildingsListComponent implements OnInit, OnDestroy {

  static readonly STORAGE_KEY = 'app-billing-buildings-list:sort';

  list = signal<BillingBuilding[]>(null);
  filteredBuildingList = signal<IBuildingList[]>(null);
  building = signal<Building>(null);

  dataTableListConfig: IDataClassConfig = {
    "building": { "valueType": "string", "title": "building", "align": "left" },
    "street": { "valueType": "string", "title": "Street", "align": "left" },
    "town": { "valueType": "string", "title": "Town", "align": "left" },
    "postcode": { "valueType": "string", "title": "Postcode", "align": "left" },
    "client": { "valueType": "string", "title": "client", "align": "left" },
    "gw": { "valueType": "number", "title": "gw", "align": "right", tooltip: "Number of gateways" },
    "org": { "valueType": "string", "title": "org", "align": "left", },
    "expires": { "valueType": "date", "dataFormat": "date", "title": "Expires", "align": "left" },
    "active": { "valueType": "boolean", "title": "Active", "align": "left" }
  };

  dataTableList = signal<DataTable>(null);

  sort: any = {};
  mustHaveGateways = true;
  isBillable = 1;

  filteredCount = signal<number>(null);
  filteredText = signal<string>(null);

  constructor(private billingService: BillingService) {
    try {
      const sort = localStorage.getItem(BillingBuildingsListComponent.STORAGE_KEY);
      if (sort) {
        this.sort = JSON.parse(sort);
      }
    } catch (e) {
      console.log(e);
    }

    const searchText = localStorage.getItem('org:billing:buildings:list:search');

    if (searchText) {
      this.filteredText.set(searchText);
    }
  }

  searchChanged(searchText: string) {
    this.filteredText.set(searchText);
    this.filterList();
  }

  billableClick() {
    this.isBillable ? this.isBillable = 0 : this.isBillable = 1;
    this.get();
  }

  ngOnInit(): void {
    this.get();
  }

  get() {
    this.billingService
      .getList("isbillable=" + this.isBillable)
      .then(r => {
        this.list.set(r.filter(b => b.building.counts.gateways >= 0));
        this.sortData();
      });
  }

  filterList() {
    const list: IBuildingList[] = this.list().map(b => {

      let title = b.building.title;

      if (b.building.isDeleted) {
        title += ' [deleted]';
      }

      let isColour = b.building.license.hasExpired || b.building.isDeleted ? 'red' : null


      let expires = b.building.license.expiresAt;
      if (expires.getFullYear() < 2000) {
        expires = null;
      }

      const client = b.billing.client?.title || null;

      const row: IBuildingList = {
        id: b.building.id,
        building: title,
        street: b.building.site.address.address1,
        town: b.building.site.address.addressTown,
        postcode: b.building.site.addressPostcode,
        client,
        gw: b.building.counts.gateways,
        org: b.building.org.shortTitle,
        expires,
        active: b.building.license.isActive,
        isColour
      };


      if (this.filteredText()?.trim()) {
        let ok = false;
        if (row.building?.toLowerCase().includes(this.filteredText().toLowerCase())) {
          ok = true;
        }
        if (row.street?.toLowerCase().includes(this.filteredText().toLowerCase())) {
          ok = true;
        }
        if (row.town?.toLowerCase().includes(this.filteredText().toLowerCase())) {
          ok = true;
        }
        if (row.postcode?.toLowerCase().includes(this.filteredText().toLowerCase())) {
          ok = true;
        }
        if (row.client?.toLowerCase().includes(this.filteredText().toLowerCase())) {
          ok = true;
        }

        if (!ok) {
          return null;
        }
      }

      return row;
    }).filter(r => r !== null);

    this.filteredBuildingList.set(list);
    this.dataTableList.set(new DataTable({ data: list, sortedColumns: null }, this.dataTableListConfig));
  }

  listItemClicked(row: any) {
    console.log(row);
    const item = this.list().find(b => b.building.id === row.id);
    if (!item) {
      return;
    }
    this.building.set(item.building)
  }

  sortData() {
    this.filterList();
    const field = Object.keys(this.sort)[0];
    const stateInt = this.sort[field] === 'asc' ? 1 : -1;
    this.list().sort((a, b) => {
      let v1, v2;
      switch (field) {
        case 'address':
          v1 = a.building.site.addressCounty;
          v2 = b.building.site.addressCounty;
          break;
        case 'gateways':
          v1 = a.building.counts.gateways;
          v2 = b.building.counts.gateways;
          break;
        case 'licenseExpiresAt':
          v1 = +a.building.license.expiresAt;
          v2 = +b.building.license.expiresAt;
          break;
        case 'org':
          v1 = a.building.org.title;
          v2 = b.building.org.title;
          break;
        case 'isActive':
          v1 = a.building.license.isActive;
          v2 = b.building.license.isActive;
          break;

        default:
          v1 = a.building[field];
          v2 = b.building[field];
          break;
      }
      return v1 > v2 ? stateInt : stateInt * -1;
    });
  }

  headerClicked(field: string) {
    let state = this.sort[field];
    this.sort = {};
    switch (state) {
      case 'asc':
        state = 'desc';
        break;
      case 'desc':
        state = null;
        break;
      default:
        state = 'asc';
        break;
    }
    this.sort[field] = state;
    localStorage.setItem(BillingBuildingsListComponent.STORAGE_KEY, JSON.stringify(this.sort));
    this.get();
  }

  ngOnDestroy(): void {
    const filteredText = this.filteredText()?.trim() ?? null;
    if (filteredText) {
      localStorage.setItem('org:billing:buildings:list:search', filteredText);
    } else {
      localStorage.removeItem('org:billing:buildings:list:search');
    }
  }
}

export interface IBuildingList {
  id: number;
  building: string;
  street: string;
  town: string;
  postcode: string;
  client: string;
  gw: number;
  org: string;
  expires: Date;
  active: boolean;
  isColour: string;
}
